import { PERMISSIONS } from '@/consts/permissions';
import { ROLES } from '@/consts/roles';
import { env } from '@/env/env';

import type { User } from './schema';

// DEV-ONLY: the full set of HSQE permission keys the frontend gates UI actions on
// via `usePermission`. Derived from the canonical PERMISSIONS registry (every
// `HSQE_`-prefixed entry) so a new HSQE capability is granted automatically the
// moment it is added there — no second list to keep in sync.
export const HSQE_DEV_GRANT_PERMISSIONS: readonly string[] = Object.entries(PERMISSIONS)
  .filter(([key]) => key.startsWith('HSQE_'))
  .map(([, value]) => value);

/**
 * Is the DEV-ONLY HSQE full-access permission grant active?
 *
 * DOUBLE-GATED, and the first gate is non-negotiable:
 *  1. `import.meta.env.DEV` — Vite hardcodes this to `false` in every production
 *     build (`vite build`, which UAT and Production both use) and `true` only under
 *     the local dev server. The whole grant branch is therefore dead code, tree-shaken
 *     out of any production bundle. This is what makes shipping the bypass to prod
 *     impossible regardless of environment variables.
 *  2. `VITE_HSQE_DEV_GRANT_ALL === 'true'` — an explicit opt-in so the grant stays off
 *     by default even locally until a developer deliberately turns it on.
 */
export const isDevPermissionGrantActive = (): boolean => import.meta.env.DEV && env.VITE_HSQE_DEV_GRANT_ALL === 'true';

/**
 * Returns `user` decorated with the full HSQE permission set when the DEV-ONLY grant
 * is active; otherwise returns `user` untouched. Never mutates its argument.
 *
 * The grant flows through the REAL permission path: it appends the HSQE keys to
 * `user.Permissions`, exactly where a fully-permissioned whoAmI response would carry
 * them, so `hasPermission` runs its normal matching logic and every HSQE `usePermission`
 * gate un-greys. It deliberately does NOT short-circuit the checks (unlike a blanket
 * `return true`), so the real permission plumbing stays exercised for every other module.
 */
export const applyDevPermissionGrant = (user: User): User =>
  isDevPermissionGrantActive() ? withHsqePermissions(user) : user;

/** Appends the full HSQE permission set to `user`, de-duplicated. Never mutates its argument. */
const withHsqePermissions = (user: User): User => ({
  ...user,
  Permissions: [...new Set([...(user.Permissions ?? []), ...HSQE_DEV_GRANT_PERMISSIONS])]
});

/**
 * UAT-ONLY (`VITE_HSQE_MOCK`): does holding `HSQE-S` imply the full HSQE permission set?
 *
 * The UAT mock artifact exists so HSQE is usable without its backend. Its per-action permissions
 * (`sms.folder.create`, `sms.document.manage-cross-links`, …) live in the MD/site database and are
 * not seeded on UAT, so every permission-gated HSQE action would stay greyed out even for a user
 * who holds `HSQE-S`. Under this flag only, the role carries its own permission set.
 *
 * DOUBLE-GATED:
 *  1. `VITE_HSQE_MOCK === 'true'` — a statically-inlined literal, so this folds to `false` and the
 *     branch is dead in every build that does not set the flag. Real Production can never take it.
 *  2. The user must actually hold `HSQE-S` — nobody else gains anything, and no other module's
 *     permissions are touched.
 *
 * This is a UAT testing affordance, NOT the product rule: in real Production `HSQE-S` alone does
 * not unlock these actions — the permissions must be seeded in MD.
 */
export const isHsqeMockRoleGrantActive = (user: User): boolean =>
  import.meta.env.VITE_HSQE_MOCK === 'true' && user.Roles.some((role) => role.Id === ROLES.HSQE_S);

/**
 * Returns `user` decorated with the full HSQE permission set when the UAT-only
 * `VITE_HSQE_MOCK` + `HSQE-S` grant applies; otherwise returns `user` untouched.
 *
 * Like the dev grant, this flows through the REAL permission path — it appends real permission
 * strings to `user.Permissions` exactly where a fully-permissioned whoAmI would carry them, so
 * `hasPermission` runs its normal matching. It never short-circuits the checks.
 */
export const applyHsqeMockRoleGrant = (user: User): User =>
  isHsqeMockRoleGrantActive(user) ? withHsqePermissions(user) : user;
